import axios from "axios";
import logger from "./logger";

const API_BASE_URL = "https://care-api.equily.ng/api";

const api = axios.create({
  baseURL: API_BASE_URL,
});

const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken");
  return {
    Authorization: `Bearer ${token}`,
    Accept: "application/json",
    "Content-Type": "application/json",
  };
};

const getCurrentUser = () => {
  // Check both possible localStorage keys for user data
  const user = localStorage.getItem("user");
  const authUser = localStorage.getItem("authUser");
  if (user) {
    try {
      return JSON.parse(user);
    } catch (e) {
      logger.warn("Failed to parse 'user' from localStorage:", e);
    }
  }

  if (authUser) {
    try {
      return JSON.parse(authUser);
    } catch (e) {
      logger.warn("Failed to parse 'authUser' from localStorage:", e);
    }
  }

  return null;
};

// Fetch current user profile from API (with role information)
const fetchUserProfile = async () => {
  try {
    const token = localStorage.getItem("authToken");
    if (!token) return null;

    const response = await api.get(`/user`, {
      headers: getAuthHeaders(),
    });

    const userData = response.data?.data || response.data;
    if (userData) {
      // Update localStorage with full user data including role
      localStorage.setItem("user", JSON.stringify(userData));
      localStorage.setItem("authUser", JSON.stringify(userData));
      if (userData.role) {
        localStorage.setItem("role", String(userData.role).toLowerCase());
      }
      return userData;
    }
    return null;
  } catch (err) {
    logger.warn("Failed to fetch user profile:", err);
    return null;
  }
};

const isSuperAdmin = () => {
  const user = getCurrentUser();
  if (!user) {
    return false;
  }

  // Check multiple possible field names and formats
  const role = user.role || user.user_role || user.role_name;
  const isSuperAdminFlag =
    user.is_super_admin || user.isSuperAdmin || user.super_admin;

  // Check role string variations
  if (role) {
    const roleLower = String(role).toLowerCase();
    if (
      roleLower === "super_admin" ||
      roleLower === "superadmin" ||
      roleLower === "super admin"
    ) {
      return true;
    }
  }
  // Check boolean/number flags
  if (
    isSuperAdminFlag === true ||
    isSuperAdminFlag === 1 ||
    isSuperAdminFlag === "1"
  ) {
    return true;
  }
  // Also check the role stored separately in localStorage
  const storedRole = localStorage.getItem("role");
  if (storedRole) {
    const roleLower = String(storedRole).toLowerCase();
    if (
      roleLower === "super_admin" ||
      roleLower === "superadmin" ||
      roleLower === "super admin"
    ) {
      return true;
    }
  }
  return false;
};

const isNormalAdmin = () => {
  const user = getCurrentUser();
  return user?.role === "admin" || user?.is_super_admin === 0;
};

const ensureSuperAdmin = async (action = "perform this action") => {
  // First check synchronously from localStorage
  if (isSuperAdmin()) {
    return;
  }
  // If not found, try fetching user profile from API
  const fetchedUser = await fetchUserProfile();
  if (fetchedUser) {
    const role =
      fetchedUser.role || fetchedUser.user_role || fetchedUser.role_name;
    const isSuperAdminFlag =
      fetchedUser.is_super_admin ||
      fetchedUser.isSuperAdmin ||
      fetchedUser.super_admin;

    const roleLower = role ? String(role).toLowerCase() : "";
    if (
      roleLower === "super_admin" ||
      roleLower === "superadmin" ||
      roleLower === "super admin" ||
      isSuperAdminFlag === true ||
      isSuperAdminFlag === 1 ||
      isSuperAdminFlag === "1"
    ) {
      return;
    }
  }

  throw new Error(`Access denied: Only Super Admins can ${action}.`);
};

const calculateAge = (dob) => {
  if (!dob) return "N/A";
  const birthDate = new Date(dob); // parse string to Date
  if (isNaN(birthDate)) return "N/A"; // check for invalid date
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};
export const loginAdmin = async (email, password) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/auth/login/admin`, {
      email,
      password,
    });

    const data = response.data;

    // ✅ Save token & user consistently
    localStorage.setItem("authToken", data.token);
    localStorage.setItem("user", JSON.stringify(data.data));
    localStorage.setItem("staffId", data.data.id);

    // ✅ Save role (super_admin or admin)
    if (data.data.role) {
      localStorage.setItem("role", data.data.role.toLowerCase());
    }

    // ✅ Fetch full user profile from API to get complete user data including role
    // This ensures we have role information even if login response doesn't include it
    try {
      const profileResponse = await api.get(`/user`, {
        headers: getAuthHeaders(),
      });
      const userProfile = profileResponse.data?.data || profileResponse.data;
      if (userProfile) {
        // Update localStorage with full user profile
        localStorage.setItem("user", JSON.stringify(userProfile));
        localStorage.setItem("authUser", JSON.stringify(userProfile));
        if (userProfile.role) {
          localStorage.setItem("role", String(userProfile.role).toLowerCase());
        }
      }
    } catch (profileErr) {
      // If profile fetch fails, continue with login data
      logger.warn("Could not fetch user profile after login:", profileErr);
    }

    return data;
  } catch (err) {
    throw new Error(err.response?.data?.message || "Login failed");
  }
};

// ---------------- AXIOS INTERCEPTOR ----------------
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken"); // ✅ always use authToken
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ---------------- METRICS ----------------
export const getAdminDashboardMetrics = async (filters = {}) => {
  try {
    const params = {};
    if (filters.from) params.from = filters.from;
    if (filters.to) params.to = filters.to;
    const response = await api.get("/admin/dashboard", {
      headers: getAuthHeaders(),
      params,
    });
    return response.data.data || response.data;
  } catch (err) {
    logger.error(
      "Failed to fetch admin dashboard metrics:",
      err.response?.data || err,
    );
    return null; // Return null so callers can handle graceful fallback or 0
  }
};

export const getEnrolmentsByLga = async (params = {}, filters = {}) => {
  try {
    const mergedParams = { ...params };
    if (filters.from) mergedParams.from = filters.from;
    if (filters.to) mergedParams.to = filters.to;
    const response = await api.get("/admin/dashboard/enrolments-by-lga", {
      headers: getAuthHeaders(),
      params: mergedParams,
    });

    return response.data || null;
  } catch (err) {
    logger.error(
      "Failed to fetch enrolment distribution by LGA:",
      err.response?.data || err,
    );
    return null;
  }
};

const downloadActiveUsersCSV = async (endpoint, filename, filters = {}) => {
  const params = {};
  if (filters.from) params.from = filters.from;
  if (filters.to) params.to = filters.to;
  const response = await api.get(endpoint, {
    headers: getAuthHeaders(),
    params,
    responseType: "blob",
  });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadDauUsers = (filters = {}) =>
  downloadActiveUsersCSV(
    "/admin/dashboard/dau-users",
    `dau_users_${new Date().toISOString().slice(0, 10)}.csv`,
    filters,
  );

export const downloadWauUsers = (filters = {}) =>
  downloadActiveUsersCSV(
    "/admin/dashboard/wau-users",
    `wau_users_${new Date().toISOString().slice(0, 10)}.csv`,
    filters,
  );

export const downloadMauUsers = (filters = {}) =>
  downloadActiveUsersCSV(
    "/admin/dashboard/mau-users",
    `mau_users_${new Date().toISOString().slice(0, 10)}.csv`,
    filters,
  );

export const downloadInactiveDailyUsers = (filters = {}) =>
  downloadActiveUsersCSV(
    "/admin/dashboard/inactive-daily-users",
    `inactive_daily_users_${new Date().toISOString().slice(0, 10)}.csv`,
    filters,
  );

export const downloadInactiveWeeklyUsers = (filters = {}) =>
  downloadActiveUsersCSV(
    "/admin/dashboard/inactive-weekly-users",
    `inactive_weekly_users_${new Date().toISOString().slice(0, 10)}.csv`,
    filters,
  );

export const downloadInactiveMonthlyUsers = (filters = {}) =>
  downloadActiveUsersCSV(
    "/admin/dashboard/inactive-monthly-users",
    `inactive_monthly_users_${new Date().toISOString().slice(0, 10)}.csv`,
    filters,
  );

export const downloadCompletionRateUsers = (filters = {}) =>
  downloadActiveUsersCSV(
    "/admin/dashboard/completion-rate-users",
    `completion_rate_${new Date().toISOString().slice(0, 10)}.csv`,
    filters,
  );

export const downloadTotalEnrollments = (filters = {}) =>
  downloadActiveUsersCSV(
    "/admin/dashboard/total-enrollments",
    `total_enrollments_${new Date().toISOString().slice(0, 10)}.csv`,
    filters,
  );

export const downloadCompletedEnrollments = (filters = {}) =>
  downloadActiveUsersCSV(
    "/admin/dashboard/completed-enrollments",
    `completed_enrollments_${new Date().toISOString().slice(0, 10)}.csv`,
    filters,
  );

export const getChartQuerySchema = async () => {
  try {
    const response = await api.get("/charts/query/schema", {
      headers: getAuthHeaders(),
    });

    return response.data?.data || null;
  } catch (err) {
    logger.error(
      "Failed to fetch chart query schema:",
      err.response?.data || err,
    );
    return null;
  }
};

export const runChartQuery = async (payload) => {
  try {
    const response = await api.post("/charts/query", payload, {
      headers: getAuthHeaders(),
    });

    return response.data || null;
  } catch (err) {
    logger.error("Failed to run chart query:", err.response?.data || err);
    throw err;
  }
};

// ---------------- COURSES ----------------
export const getCourses = async ({
  page = 1,
  limit = 20,
  status = "all",
  from = "",
  to = "",
} = {}) => {
  const params = new URLSearchParams({ page, limit });
  if (status !== "all") params.append("status", status);
  if (from) params.append("from", from);
  if (to) params.append("to", to);

  const response = await api.get(`/courses?${params.toString()}`);
  return response.data.data.map((course) => ({
    id: course.id,
    title: course.title,
    description: course.description || "No description provided.",
    category: course.category?.name || "Uncategorized",
    learners_count: course.learners_count || 0,
    modules_count: course.modules_count || 0,
    completed: course.completed_learners ?? 0,
    active_learners: course.active_learners ?? 0,
    pending: course.pending ?? 0,
    tests_count: course.tests_count ?? 0,
    questions_count: course.questions_count ?? 0,
    tests_avg_percentile: course.tests_avg_percentile ?? null,
    drop_off_rate: course.drop_off_rate ?? 0,
    is_published: course.is_published,
    is_archived: course.is_archived,
    published_at: course.published_at || null,
    created_at: course.created_at,
    updated_at: course.updated_at,
    image_url: course.image_url || "/fallback.jpg",
    thumbnail_url: course.thumbnail_url || course.image_url || "/fallback.jpg",
    job_titles: course.job_titles || course.cadres || course.jobTitles || [],
    cadres: course.cadres || course.job_titles || course.jobTitles || [],
  }));
};

export const getCourseById = async (id) => {
  const res = await api.get(`/courses/${id}`);
  return res.data.data || res.data;
};

// ---------------- STATES, LGAS, FACILITIES ----------------
// Cache for location data to reduce API calls
let statesCache = null;
let lgasCache = {};
let facilitiesCache = {};
// In-flight request promises, keyed the same way as the caches above, so
// concurrent callers (e.g. React StrictMode double-invoking effects) share
// a single network request instead of firing duplicates.
let lgasInFlight = {};
let facilitiesInFlight = {};

export const getLgasByState = async (stateId) => {
  if (!stateId) return [];

  const cacheKey = String(stateId);
  if (lgasCache[cacheKey]) return lgasCache[cacheKey];
  if (lgasInFlight[cacheKey]) return lgasInFlight[cacheKey];

  const request = (async () => {
    try {
      const response = await api.get(`/locations/get/lgas/${stateId}`, {
        headers: getAuthHeaders(),
      });

      const payload = response.data;
      let lgas = [];

      if (Array.isArray(payload)) lgas = payload;
      else if (Array.isArray(payload?.data)) lgas = payload.data;
      else if (Array.isArray(payload?.data?.data)) lgas = payload.data.data;

      lgasCache[cacheKey] = lgas;
      return lgas;
    } catch (err) {
      logger.error("Failed to fetch LGAs for state:", err.response?.data || err);
      return [];
    } finally {
      delete lgasInFlight[cacheKey];
    }
  })();

  lgasInFlight[cacheKey] = request;
  return request;
};

export const getFacilities = async (lgaId = null, limit = 1000) => {
  // If we want all facilities (no lgaId), use a specific cache key
  const cacheKey = lgaId ? `lga_${lgaId}` : "all_facilities";

  if (facilitiesCache[cacheKey]) return facilitiesCache[cacheKey];
  if (facilitiesInFlight[cacheKey]) return facilitiesInFlight[cacheKey];

  facilitiesInFlight[cacheKey] = getFacilitiesRequest(cacheKey, lgaId, limit);
  try {
    return await facilitiesInFlight[cacheKey];
  } finally {
    delete facilitiesInFlight[cacheKey];
  }
};

const getFacilitiesRequest = async (cacheKey, lgaId, limit) => {
  try {
    let allData = [];
    let page = 1;
    let hasMore = true;

    // Safety break to prevent infinite loops (max 5000-10000 records approx)
    const MAX_PAGES = 100;

    while (hasMore && page <= MAX_PAGES) {
      const params = [];
      if (lgaId) params.push(`lga_id=${lgaId}`);
      if (limit) params.push(`per_page=${limit}`); // Try to request max
      params.push(`page=${page}`);

      const queryString = params.length > 0 ? `?${params.join("&")}` : "";

      const response = await api.get(`/facilities${queryString}`, {
        headers: getAuthHeaders(),
      });

      // Extract data based on response structure
      let pageData = [];
      let lastPage = 1;

      if (Array.isArray(response.data)) {
        pageData = response.data;
        hasMore = false; // Simple array usually means no pagination
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        pageData = response.data.data;
        const meta = response.data.meta || response.data;
        lastPage = meta.last_page || 1;
      } else if (response.data && Array.isArray(response.data.data)) {
        pageData = response.data.data;
        lastPage = response.data.last_page || 1;
      }

      if (!pageData || pageData.length === 0) {
        break;
      }

      allData = [...allData, ...pageData];

      // Check if we reached the last page or if we got fewer items than expected
      if (page >= lastPage || (limit && pageData.length < 15)) {
        hasMore = false;
      } else {
        page++;
      }
    }

    facilitiesCache[cacheKey] = allData;
    return allData;
  } catch (err) {
    logger.error("Failed to fetch facilities:", err.response?.data || err);
    return [];
  }
};

export const getJobTitles = async () => {
  try {
    const response = await api.get('/job_titles', {
      headers: getAuthHeaders(),
    });

    const payload = response.data;

    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    if (Array.isArray(payload?.data?.job_titles)) return payload.data.job_titles;
    if (Array.isArray(payload?.job_titles)) return payload.job_titles;

    return [];
  } catch (err) {
    logger.error('Failed to fetch job titles:', err.response?.data || err);
    return [];
  }
};

export const getLanguages = async () => {
  try {
    const response = await api.get('/languages', {
      headers: getAuthHeaders(),
    });

    const payload = response.data;

    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.data)) return payload.data.data;

    return [];
  } catch (err) {
    logger.error('Failed to fetch languages:', err.response?.data || err);
    return [];
  }
};

let statesInFlight = null;

export const getStates = async () => {
  if (statesCache) return statesCache;
  if (statesInFlight) return statesInFlight;

  statesInFlight = (async () => {
    try {
      const response = await api.get('/locations/get/states', {
        headers: getAuthHeaders(),
      });

      const payload = response.data;
      let states = [];

      if (Array.isArray(payload)) states = payload;
      else if (Array.isArray(payload?.data)) states = payload.data;
      else if (Array.isArray(payload?.data?.data)) states = payload.data.data;

      statesCache = states;
      return states;
    } catch (err) {
      logger.error('Failed to fetch states:', err.response?.data || err);
      return [];
    } finally {
      statesInFlight = null;
    }
  })();

  return statesInFlight;
};

// Deprecated: no backend equivalent, use getLgasByState(stateId) instead
export const getLGAs = async () => [];

// Clear location caches (useful for refreshing data)
export const clearLocationCache = () => {
  statesCache = null;
  lgasCache = {};
  facilitiesCache = {};
};

// ---------------- LEARNERS ----------------
export const getLearners = async (filters = {}) => {
  const params = { is_admin: 0 };
  if (filters.from) params.from = filters.from;
  if (filters.to) params.to = filters.to;
  if (filters.page) params.page = filters.page;
  if (filters.per_page) params.per_page = filters.per_page;
  if (filters.search) params.search = filters.search;

  const response = await api.get(`/learners`, { params });
  const resData = response.data.data;

  // Backend returns a Laravel paginator: { current_page, data: [...], last_page, per_page, total }
  if (resData && Array.isArray(resData.data)) {
    return {
      records: resData.data,
      pagination: {
        currentPage: resData.current_page || 1,
        lastPage: resData.last_page || 1,
        perPage: resData.per_page || 50,
        total: resData.total || 0,
      },
    };
  }

  // Fallback for a plain array response
  const records = Array.isArray(resData) ? resData : [];
  return {
    records,
    pagination: { currentPage: 1, lastPage: 1, perPage: 50, total: records.length },
  };
};

/**
 * Fetch all health workers pre-enriched with enrolments and activity data
 * in a single request, eliminating the per-user N+1 fan-out on the frontend.
 *
 * @param {Object}   filters             - Optional filter params
 * @param {string}   filters.from        - Start date (YYYY-MM-DD)
 * @param {string}   filters.to          - End date (YYYY-MM-DD)
 * @param {number[]} filters.ids         - Restrict to specific staff IDs
 * @param {number}   filters.lga_id      - Restrict to a specific LGA
 * @param {number}   filters.facility_id - Restrict to a specific health facility
 */
export const getHealthWorkersExport = async (filters = {}) => {
  const params = {};
  if (filters.from) params.from = filters.from;
  if (filters.to) params.to = filters.to;
  if (Array.isArray(filters.ids) && filters.ids.length > 0) {
    params['ids[]'] = filters.ids;
  }
  if (filters.lga_id) params.lga_id = filters.lga_id;
  if (filters.facility_id) params.facility_id = filters.facility_id;
  const response = await api.get('/export/health-workers', { params });
  return response.data.data || [];
};

export const getLearnerById = async (id) => {
  try {
    const response = await api.get(`/learners/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data.data || response.data;
  } catch (err) {
    logger.error(`Failed to fetch learner ${id}:`, err.response?.data || err);
    throw err.response?.data || { message: "Failed to fetch learner" };
  }
};

// Paginated version of getLearners
export const getLearnersPaginated = async (
  page = 1,
  limit = 25,
  search = "",
  filters = {},
) => {
  try {
    const params = { page, per_page: limit, is_admin: 0 };
    if (search) params.search = search;
    if (filters.from) params.from = filters.from;
    if (filters.to) params.to = filters.to;

    const response = await api.get(`/learners`, { params });
    const data = response.data;

    // Normalize response to consistent format
    return {
      data: data.data || [],
      meta: {
        page: data.current_page || page,
        limit: data.per_page || limit,
        total: data.total || 0,
        totalPages: data.last_page || 1,
      },
    };
  } catch (err) {
    logger.error("Failed to fetch learners:", err.response?.data || err);
    throw err.response?.data || { message: "Failed to fetch learners" };
  }
};

export const getLearnersById = async (id) => {
  const response = await api.get(`/learners/${id}`);
  const learner = response.data.data;
  return {
    ...learner,
    job_title_name: learner.job_title_id
      ? `Job Title #${learner.job_title_id}`
      : "N/A",
    facility_name: learner.facility_id
      ? `Facility #${learner.facility_id}`
      : "N/A",
    lga_name: learner.lga_id ? `LGA #${learner.lga_id}` : "N/A",
    completed: learner.completed ?? 0,
    pending: learner.pending ?? 0,
    tests_count: learner.tests_count ?? 0,
    questions_count: learner.questions_count ?? 0,
    tests_avg_percentile: learner.tests_avg_percentile ?? null,
  };
};

// ---------------- UPDATE LEARNER ----------------
export async function updateLearner(id, data) {
  await ensureSuperAdmin("Update Learners");
  const normalizedLgaId = data.lga_id ?? data.lgaId;
  const normalizedStateId = data.state_id ?? data.stateId;
  const normalizedFacilityId = data.facility_id ?? data.facilityId;
  const normalizedQualification =
    data.highest_qualification ?? data.highestQualification;

  const payload = {
    firstname: data.firstname,
    othernames: data.othernames || null,
    lastname: data.lastname,
    email: data.email,
    phone_number: data.phone_number || "0000000000",
    gender: data.gender,
    age: data.age || null,
    lga_id: normalizedLgaId ? Number(normalizedLgaId) : null,
    state_id: normalizedStateId ? Number(normalizedStateId) : null,
    facility_id: normalizedFacilityId ? Number(normalizedFacilityId) : null,
    dob: data.dob || null,
    highest_qualification: normalizedQualification || null,
  };

  const res = await api.put(`/learners/${id}`, payload);
  return res.data;
}
export const getQuizById = async (id) => {
  try {
    const res = await api.get(`/admin/quizes/${id}`, {
      headers: getAuthHeaders(),
    });
    return res.data.data || res.data;
  } catch (err) {
    logger.error("Error fetching quiz details:", err.response?.data || err);
    throw err;
  }
};

// ---------------- CREATE HEALTH WORKER ----------------
export async function createHealthWorker(data) {
  await ensureSuperAdmin("create user");
  // Validate required fields (password is now optional - backend may auto-generate)
  if (
    !data.firstname ||
    !data.lastname ||
    !data.phone_number ||
    !data.gender ||
    !data.job_title_id
  ) {
    throw new Error(
      "Missing required fields: firstname, lastname, phone_number, gender, and job_title_id are required.",
    );
  }

  // Normalize gender to lowercase (backend accepts case-insensitive)
  const normalizedGender = String(data.gender).toLowerCase();
  if (!["male", "female", "other"].includes(normalizedGender)) {
    throw new Error('Invalid gender. Must be "male", "female", or "other".');
  }

  // Validate password length if provided
  if (data.password && data.password.length < 6) {
    throw new Error("Password must be at least 6 characters long.");
  }

  // Build payload according to API requirements
  const normalizedLgaId = data.lga_id ?? data.lgaId;
  const normalizedStateId = data.state_id ?? data.stateId;
  const normalizedFacilityId = data.facility_id ?? data.facilityId;
  const normalizedQualification =
    data.highest_qualification ?? data.highestQualification;

  const payload = {
    // REQUIRED FIELDS
    firstname: String(data.firstname).trim(),
    lastname: String(data.lastname).trim(),
    phone_number: String(data.phone_number).trim(),
    gender: normalizedGender,
    job_title_id: Number(data.job_title_id),
    // OPTIONAL FIELDS
    email: data.email && data.email.trim() ? String(data.email).trim() : null,
    othernames:
      data.othernames && data.othernames.trim()
        ? String(data.othernames).trim()
        : null,
    password:
      data.password && data.password.trim() ? String(data.password) : null,
    facility_id: normalizedFacilityId ? Number(normalizedFacilityId) : null,
    lga_id: normalizedLgaId ? Number(normalizedLgaId) : null,
    state_id: normalizedStateId ? Number(normalizedStateId) : null,
    dob: data.dob || null,
    highest_qualification: normalizedQualification || null,
    // NOTE: agency_id is NOT included - column doesn't exist in database
    is_admin: data.is_admin !== undefined ? (data.is_admin ? 1 : 0) : 0,
    is_active: data.is_active !== undefined ? (data.is_active ? 1 : 0) : 1,
  };

  // Remove null password if not provided (let backend handle default)
  if (!payload.password) {
    delete payload.password;
  }

  // Explicitly remove agency_id if it exists in data (shouldn't, but just in case)
  if ("agency_id" in payload) {
    delete payload.agency_id;
  }
  if (data.agency_id !== undefined) {
    // Don't include it
  }

  try {
    const res = await api.post(`/auth/register/learner`, payload, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    return res.data;
  } catch (err) {
    const errorMessage =
      err.response?.data?.message || err.response?.data?.error || err.message;
    logger.error(
      "❌ Error creating health worker:",
      err.response?.data || err.message,
    );
    throw new Error(errorMessage || "Failed to create health worker");
  }
}

export const getModules = async (courseId) => {
  const res = await api.get(`/modules`, {
    params: { course_id: courseId },
    headers: getAuthHeaders(),
  });
  return res.data?.data || [];
};

export const updateModule = async (id, moduleData) => {
  await ensureSuperAdmin("update modules");
  const token = localStorage.getItem("authToken");
  const response = await axios.put(`${API_BASE_URL}/modules/${id}`, moduleData, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

export const deleteModule = async (id) => {
  await ensureSuperAdmin("delete modules");
  const token = localStorage.getItem("authToken");
  const response = await axios.delete(`${API_BASE_URL}/modules/${id}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    },
  });
  return response.data;
};

export const getTopics = async (moduleId) => {
  const res = await api.get(`/topics`, {
    params: { module_id: moduleId },
    headers: getAuthHeaders(),
  });
  return res.data?.data || [];
};

export const updateTopic = async (id, topicData) => {
  await ensureSuperAdmin("update topics");
  const token = localStorage.getItem("authToken");
  const response = await axios.put(`${API_BASE_URL}/topics/${id}`, topicData, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

export const deleteTopic = async (id) => {
  await ensureSuperAdmin("delete topics");
  const token = localStorage.getItem("authToken");
  const response = await axios.delete(`${API_BASE_URL}/topics/${id}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    },
  });
  return response.data;
};

export const getLanguageLessons = async (topicId) => {
  const res = await api.get(`/language_lessons`, {
    params: { topic_id: topicId },
    headers: getAuthHeaders(),
  });
  return res.data || [];
};

export const createLanguageLesson = async (lessonData) => {
  await ensureSuperAdmin("create language lessons");
  const token = localStorage.getItem("authToken");
  const headers = lessonData instanceof FormData
    ? { Authorization: `Bearer ${token}` }
    : { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const response = await axios.post(`${API_BASE_URL}/language_lessons`, lessonData, { headers });
  return response.data;
};

export const updateLanguageLesson = async (id, lessonData) => {
  await ensureSuperAdmin("update language lessons");
  const token = localStorage.getItem("authToken");
  const headers = lessonData instanceof FormData
    ? { Authorization: `Bearer ${token}` }
    : { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const response = await axios.post(
    `${API_BASE_URL}/language_lessons/${id}?_method=PUT`,
    lessonData,
    { headers },
  );
  return response.data;
};

export const deleteLanguageLesson = async (id) => {
  await ensureSuperAdmin("delete language lessons");
  const token = localStorage.getItem("authToken");
  const response = await axios.delete(`${API_BASE_URL}/language_lessons/${id}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    },
  });
  return response.data;
};

// ---------------- CREATE QUIZ (TEST) ----------------
export const createTest = async (payload) => {
  await ensureSuperAdmin("create quiz");
  try {
    const headers = {};
    // Handle FormData or JSON
    if (payload instanceof FormData) {
      // FormData - let browser set Content-Type with boundary
      const token = localStorage.getItem("authToken");
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    } else {
      // JSON payload
      headers["Content-Type"] = "application/json";
      const token = localStorage.getItem("authToken");
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    const response = await axios.post(`${API_BASE_URL}/tests`, payload, {
      headers,
    });

    const responseData = response.data;
    logger.debug("Test created successfully:", responseData);

    if (responseData.success === false) {
      const error = new Error(responseData.message || "Test creation failed");
      error.response = response;
      throw error;
    }

    return responseData.data || responseData;
  } catch (err) {
    logger.error("Failed to create test:", err.response?.data || err);
    if (err.response?.data) {
      const apiError = err.response.data;
      if (apiError.message) {
        err.message = apiError.message;
      }
    }
    throw err;
  }
};

// Update Test/Quiz
export const updateTest = async (id, payload) => {
  try {
    await ensureSuperAdmin("update test");

    const headers = {};

    // Handle FormData or JSON
    if (payload instanceof FormData) {
      // FormData - let browser set Content-Type with boundary
      const token = localStorage.getItem("authToken");
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    } else {
      // JSON payload
      headers["Content-Type"] = "application/json";
      const token = localStorage.getItem("authToken");
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    const response = await axios.put(`${API_BASE_URL}/tests/${id}`, payload, {
      headers,
    });

    const responseData = response.data;
    logger.debug("Test updated successfully:", responseData);

    if (responseData.success === false) {
      const error = new Error(responseData.message || "Test update failed");
      error.response = response;
      throw error;
    }

    return responseData.data || responseData;
  } catch (err) {
    logger.error("Failed to update test:", err.response?.data || err);
    if (err.response?.data) {
      const apiError = err.response.data;
      if (apiError.message) {
        err.message = apiError.message;
      }
    }
    throw err;
  }
};

// Delete Test/Quiz
export const deleteTest = async (id) => {
  try {
    await ensureSuperAdmin("delete test");
    const token = localStorage.getItem("authToken");
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    };

    const response = await axios.delete(`${API_BASE_URL}/tests/${id}`, {
      headers,
    });

    const responseData = response.data;
    logger.debug("Test deleted successfully:", responseData);

    if (responseData.success === false) {
      const error = new Error(responseData.message || "Test deletion failed");
      error.response = response;
      throw error;
    }

    return responseData.data || responseData;
  } catch (err) {
    logger.error("Failed to delete test:", err.response?.data || err);
    if (err.response?.data) {
      const apiError = err.response.data;
      if (apiError.message) {
        err.message = apiError.message;
      }
    }
    throw err;
  }
};

// List Tests/Quizzes with optional filters
/**
 * Fetch quiz export data grouped by learner × course.
 * Returns one row per (staff, course) with first pre-test, last post-test,
 * and percentage gained — server-side aggregation avoids raw test fan-out.
 *
 * @param {string|number|null} courseId - Optional course filter
 */
export const getQuizzesExport = async (courseId = null) => {
  const params = {};
  if (courseId) params.course_id = courseId;
  const response = await api.get('/export/quizzes', { params });
  return response.data.data || [];
};

export const getTests = async (
  courseId = null,
  moduleId = null,
  staffId = null,
) => {
  try {
    const params = new URLSearchParams();
    if (courseId) {
      params.append("course_id", courseId);
    }
    if (moduleId) {
      params.append("module_id", moduleId);
    }
    if (staffId) {
      params.append("staff_id", staffId);
    }

    const queryString = params.toString();
    const url = queryString
      ? `${API_BASE_URL}/tests?${queryString}`
      : `${API_BASE_URL}/tests`;

    const response = await axios.get(url, {
      headers: getAuthHeaders(),
    });

    const responseData = response.data;
    logger.debug("Tests fetched successfully:", responseData);

    if (responseData.success === false) {
      const error = new Error(responseData.message || "Failed to fetch tests");
      error.response = response;
      throw error;
    }

    return responseData.data || responseData;
  } catch (err) {
    logger.error("Failed to fetch tests:", err.response?.data || err);
    if (err.response?.data) {
      const apiError = err.response.data;
      if (apiError.message) {
        err.message = apiError.message;
      }
    }
    throw err;
  }
};

// ---------------------- QUESTIONS ----------------------
export const createQuestion = async (questionData) => {
  await ensureSuperAdmin("create question");
  try {
    // ✅ Keep options as an array, not string
    const payload = {
      ...questionData,
      options: Array.isArray(questionData.options) ? questionData.options : [], // fallback to empty array
    };

    const token = localStorage.getItem("authToken");

    const response = await axios.post(`${API_BASE_URL}/questions`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (err) {
    logger.error("Failed to create question:", err.response?.data || err);
    throw err.response?.data || { message: "Failed to create question" };
  }
};

// Get Question by ID
export const getQuestion = async (id) => {
  try {
    const response = await api.get(`/questions/${id}`, {
      headers: getAuthHeaders(),
    });

    const responseData = response.data;
    logger.debug("Question fetched successfully:", responseData);

    if (responseData.success === false) {
      const error = new Error(
        responseData.message || "Failed to fetch question",
      );
      error.response = response;
      throw error;
    }

    return responseData.data || responseData;
  } catch (err) {
    logger.error("Failed to fetch question:", err.response?.data || err);
    throw err.response?.data || { message: "Failed to fetch question" };
  }
};

// Get all questions (with optional course_id, module_id filter and pagination)
// Only returns questions from published quizzes/tests
export const getQuestions = async (
  courseId = null,
  testId = null,
  moduleId = null,
  page = 1,
  limit = 100,
) => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    // Filter by test_id if provided, otherwise try course_id
    if (testId) {
      params.append("test_id", testId);
    } else if (courseId) {
      // Try course_id filter - if backend doesn't support it, we'll filter client-side
      params.append("course_id", courseId);
    }
    // Add module_id filter if provided
    // if (moduleId) {
    //   params.append("module_id", moduleId);
    // }

    const url = `/questions?${params.toString()}`;
    logger.debug(`Fetching questions from: ${url}`);

    const response = await api.get(url, {
      headers: getAuthHeaders(),
    });

    // Handle paginated response
    const responseData = response.data;
    logger.debug(`API response structure:`, {
      hasData: !!responseData?.data,
      isArray: Array.isArray(responseData),
      dataIsArray: Array.isArray(responseData?.data),
      dataLength: responseData?.data?.length || responseData?.length || 0,
      fullResponse: responseData,
    });

    // Extract questions from response
    let questions = [];
    let rawQuestions = [];

    if (responseData?.data && Array.isArray(responseData.data)) {
      rawQuestions = responseData.data;
      logger.debug(
        `Found ${rawQuestions.length} questions in response.data.data`,
      );
    } else if (Array.isArray(responseData)) {
      rawQuestions = responseData;
      logger.debug(
        `Found ${rawQuestions.length} questions in response.data (direct array)`,
      );
    } else {
      logger.warn(`Unexpected response format:`, responseData);
    }

    // Filter questions based on criteria
    if (courseId) {
      // When course_id is provided, the backend should filter correctly
      // We'll do minimal client-side verification to ensure questions match the course
      const courseIdNum = parseInt(courseId);
      const courseIdStr = String(courseId);

      questions = rawQuestions.filter((q) => {
        // Get course_id from question - check multiple possible locations
        const questionCourseId =
          q.course_id ||
          q.course?.id ||
          q.course?.course_id ||
          q.test?.course_id;

        // If we have a course_id on the question, verify it matches
        if (questionCourseId) {
          const qCourseIdNum = parseInt(questionCourseId);
          const qCourseIdStr = String(questionCourseId);

          // Must match the requested course_id
          const matchesCourse =
            qCourseIdNum === courseIdNum ||
            qCourseIdStr === courseIdStr ||
            questionCourseId == courseId;

          if (!matchesCourse) {
            return false;
          }
        }

        // Trust the API's filtering - if backend returned it with course_id param, it's valid
        // Only apply published filter if we have explicit published info
        if (q.course && q.course.hasOwnProperty("is_published")) {
          const isPublished =
            q.course.is_published === true ||
            q.course.is_published === 1 ||
            q.course.is_published === "1";
          return isPublished;
        }

        // If no published info, trust the API (backend filtered by course_id)
        return true;
      });

      logger.debug(
        `API returned ${rawQuestions.length} questions, filtered to ${questions.length} for course_id=${courseId}`,
      );
      if (rawQuestions.length > 0 && questions.length === 0) {
        logger.warn(
          `No questions matched course ${courseId}. Sample course_ids from questions:`,
          rawQuestions.slice(0, 3).map((q) => ({
            course_id: q.course_id,
            course_id_from_course: q.course?.id,
            course_course_id: q.course?.course_id,
            test_course_id: q.test?.course_id,
            course_published: q.course?.is_published,
          })),
        );
      }
    } else if (testId) {
      // When filtering by test_id, trust the API response
      // The backend already filters by test_id, so return all questions returned
      // Only do minimal filtering for published status if test relationship is loaded
      questions = rawQuestions.filter((q) => {
        // If test relationship is loaded, only include if published
        // If test relationship is not loaded, trust the API and include it
        if (q.test && q.test.hasOwnProperty("is_published")) {
          return (
            q.test.is_published === true ||
            q.test.is_published === 1 ||
            q.test.is_published === "1"
          );
        }
        // No test info - trust the API response
        return true;
      });

      logger.debug(
        `API returned ${rawQuestions.length} questions for test_id=${testId}, after published filter: ${questions.length}`,
      );
    } else {
      // No filter - only show published
      questions = rawQuestions.filter((q) => {
        return (
          !q.test || q.test.is_published === true || q.test.is_published === 1
        );
      });
    }

    logger.debug(
      `Fetched ${rawQuestions.length} raw questions, filtered to ${questions.length} for courseId: ${courseId}, testId: ${testId}`,
    );

    // Check if response has pagination structure
    if (
      responseData?.data &&
      (responseData.meta || responseData.total !== undefined)
    ) {
      return {
        questions: questions,
        total: questions.length, // Use filtered count
        currentPage:
          responseData.meta?.current_page || responseData.current_page || page,
        lastPage: responseData.meta?.last_page || responseData.last_page || 1,
        perPage: responseData.meta?.per_page || responseData.per_page || limit,
      };
    }

    // Return filtered questions for non-paginated response
    return questions;
  } catch (err) {
    logger.error("Failed to fetch questions:", err.response?.data || err);
    throw err.response?.data || { message: "Failed to fetch questions" };
  }
};

// Update a question
export const updateQuestion = async (questionId, questionData) => {
  try {
    await ensureSuperAdmin("update question");

    const headers = {};

    // Handle FormData or JSON
    if (questionData instanceof FormData) {
      // FormData - let browser set Content-Type with boundary
      const token = localStorage.getItem("authToken");
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    } else {
      // JSON payload
      headers["Content-Type"] = "application/json";
      const token = localStorage.getItem("authToken");
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    const payload =
      questionData instanceof FormData
        ? questionData
        : {
            ...questionData,
            options: Array.isArray(questionData.options)
              ? questionData.options
              : [],
          };

    const response = await axios.put(
      `${API_BASE_URL}/questions/${questionId}`,
      payload,
      {
        headers,
      },
    );

    const responseData = response.data;
    logger.debug(`Successfully updated question ${questionId}:`, responseData);

    if (responseData.success === false) {
      const error = new Error(responseData.message || "Question update failed");
      error.response = response;
      throw error;
    }

    return responseData.data || responseData;
  } catch (err) {
    logger.error("Failed to update question:", err.response?.data || err);

    const errorMessage =
      err.response?.data?.message ||
      err.response?.data?.error ||
      err.message ||
      "Failed to update question. Please try again.";

    throw { message: errorMessage };
  }
};

// Delete a question
export const deleteQuestion = async (questionId) => {
  try {
    await ensureSuperAdmin("delete question");

    const response = await api.delete(`/questions/${questionId}`, {
      headers: getAuthHeaders(),
    });

    logger.debug(`Successfully deleted question ${questionId}:`, response.data);
    return response.data;
  } catch (err) {
    logger.error("Failed to delete question:", err.response?.data || err);

    // Handle specific error cases
    if (
      err.response?.status === 403 ||
      err.message?.includes("Access denied")
    ) {
      throw {
        message: "Access denied: Only Super Admins can delete questions.",
      };
    }

    if (err.response?.status === 404) {
      throw {
        message: "Question not found. It may have already been deleted.",
      };
    }

    if (err.response?.status === 500) {
      throw { message: "Server error. Please try again later." };
    }

    // Extract error message from response
    const errorMessage =
      err.response?.data?.message ||
      err.response?.data?.error ||
      err.message ||
      "Failed to delete question. Please try again.";

    throw { message: errorMessage };
  }
};

// ---------------- FETCH QUIZZES ----------------
export const fetchQuizzes = async () => {
  try {
    const res = await api.get(`/admin/quizes`);
    // Use backend fields exactly as they are
    return {
      data: res.data.data.map((item) => ({
        id: item.id,
        title: item.title,
        category_id: item.category_id,
        job_title_id: item.job_title_id,
        description: item.description,
        completed: item.completed ?? 0,
        pending: item.pending ?? 0,
        tests_count: item.tests_count ?? 0,
        questions_count: item.questions_count ?? 0,
        tests_avg_percentile: item.tests_avg_percentile,
        image_url: item.image_url,
        is_published: item.is_published,
      })),
    };
  } catch (err) {
    logger.error("Error fetching quizzes:", err);
    return { data: [] };
  }
};

export const updateCourse = async (id, data) => {
  // Note: API docs indicate this is a public route (no auth required)
  // But we'll keep auth for now in case backend still requires it
  try {
    const headers = {};
    // If data is FormData, send as FormData (for file uploads)
    // If it's a regular object, send as JSON with nested structure
    if (data instanceof FormData) {
      // FormData - let browser set Content-Type with boundary
      // Still try to add auth if available (backend might still require it)
      const token = localStorage.getItem("authToken");
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    } else {
      // JSON payload - match API specification
      headers["Content-Type"] = "application/json";
      // Try auth if available
      const token = localStorage.getItem("authToken");
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    logger.debug("Updating course:", id);
    logger.debug(
      "Payload type:",
      data instanceof FormData ? "FormData" : "JSON",
    );

    // Validate data is not empty
    if (data instanceof FormData) {
      const formDataEntries = Array.from(data.entries());
      logger.debug("FormData entries count:", formDataEntries.length);
      if (formDataEntries.length === 0) {
        throw new Error(
          "No data provided for update. Please send the fields you want to update.",
        );
      }
      logger.debug(
        "FormData entries:",
        formDataEntries.map(([key, val]) => [
          key,
          val instanceof File ? `File: ${val.name}` : val,
        ]),
      );
    } else {
      if (!data || Object.keys(data).length === 0) {
        throw new Error(
          "No data provided for update. Please send the fields you want to update.",
        );
      }
      logger.debug("Payload structure:", JSON.stringify(data, null, 2));
      logger.debug("Payload keys:", Object.keys(data));
    }

    const response = await axios.put(`${API_BASE_URL}/courses/${id}`, data, {
      headers,
    });

    // Handle API response structure: { success, message, data }
    const responseData = response.data;
    logger.debug("Course updated successfully:", responseData);

    // Check if response indicates success
    if (responseData.success === false) {
      const error = new Error(responseData.message || "Course update failed");
      error.response = response;
      throw error;
    }

    // Return the full response structure or just the data
    return responseData.data || responseData;
  } catch (err) {
    logger.error("updateCourse error:", err);
    logger.error("Error response:", err.response?.data);

    // Extract error message from API response if available
    if (err.response?.data) {
      const apiError = err.response.data;
      if (apiError.message) {
        err.message = apiError.message;
      } else if (apiError.error) {
        err.message = apiError.error;
      }
    }
    throw err;
  }
};

// Delete Course
export const deleteCourse = async (id) => {
  try {
    const token = localStorage.getItem("authToken");
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    };

    const response = await axios.delete(`${API_BASE_URL}/courses/${id}`, {
      headers,
    });

    // Handle API response structure: { success, message, data }
    const responseData = response.data;
    logger.debug("Course deleted successfully:", responseData);

    if (responseData.success === false) {
      const error = new Error(responseData.message || "Course deletion failed");
      error.response = response;
      throw error;
    }

    return responseData.data || responseData;
  } catch (err) {
    logger.error("deleteCourse error:", err);
    if (err.response?.data) {
      const apiError = err.response.data;
      if (apiError.message) {
        err.message = apiError.message;
      }
    }
    throw err;
  }
};

// Archive Course
export const archiveCourse = async (id) => {
  try {
    const token = localStorage.getItem("authToken");
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    };

    const response = await axios.post(
      `${API_BASE_URL}/courses/${id}/archive`,
      {},
      {
        headers,
      },
    );

    const responseData = response.data;
    logger.debug("Course archived successfully:", responseData);

    if (responseData.success === false) {
      const error = new Error(
        responseData.message || "Course archiving failed",
      );
      error.response = response;
      throw error;
    }

    return responseData.data || responseData;
  } catch (err) {
    logger.error("archiveCourse error:", err);
    if (err.response?.data) {
      const apiError = err.response.data;
      if (apiError.message) {
        err.message = apiError.message;
      }
    }
    throw err;
  }
};

// Unarchive Course
export const unarchiveCourse = async (id) => {
  try {
    const token = localStorage.getItem("authToken");
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    };

    const response = await axios.post(
      `${API_BASE_URL}/courses/${id}/unarchive`,
      {},
      {
        headers,
      },
    );

    const responseData = response.data;
    logger.debug("Course unarchived successfully:", responseData);

    if (responseData.success === false) {
      const error = new Error(
        responseData.message || "Course unarchiving failed",
      );
      error.response = response;
      throw error;
    }

    return responseData.data || responseData;
  } catch (err) {
    logger.error("unarchiveCourse error:", err);
    if (err.response?.data) {
      const apiError = err.response.data;
      if (apiError.message) {
        err.message = apiError.message;
      }
    }
    throw err;
  }
};

export const uploadFile = async (file) => {
  await ensureSuperAdmin("upload courses");
  const formData = new FormData();
  formData.append("file", file);

  const token = localStorage.getItem("authToken");
  // Try multiple common upload endpoint patterns
  const uploadEndpoints = [
    "/upload",
    "/files/upload",
    "/media/upload",
    "/storage/upload",
    "/api/upload",
    "/api/files/upload",
    "/api/media/upload",
  ];

  let lastError = null;

  for (const endpoint of uploadEndpoints) {
    try {
      logger.debug(`Attempting upload to: ${API_BASE_URL}${endpoint}`);
      const response = await axios.post(
        `${API_BASE_URL}${endpoint}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            // DO NOT set Content-Type manually. Let axios/browser set it with boundary.
          },
        },
      );

      logger.debug(`Upload successful via: ${endpoint}`, response.data);
      return response.data; // { status, message, data: { url, path, filename } }
    } catch (err) {
      lastError = err;
      // If it's a 404, try next endpoint
      if (err.response?.status === 404) {
        logger.debug(`Endpoint ${endpoint} returned 404, trying next...`);
        continue;
      }
      // If it's a different error (401, 403, 500, etc.), throw immediately
      logger.error(
        `Upload failed at ${endpoint}:`,
        err.response?.status,
        err.response?.data,
      );
      throw err;
    }
  }

  // If all endpoints failed with 404, throw a helpful error
  if (lastError?.response?.status === 404) {
    const error = new Error(
      "File upload endpoint not found. Please contact the backend team to implement an upload endpoint.",
    );
    error.response = lastError.response;
    throw error;
  }

  throw lastError || new Error("File upload failed");
};

export const createCourse = async (coursePayload) => {
  // Note: API docs indicate this is a public route (no auth required)
  // But we'll keep auth for now in case backend still requires it
  try {
    // If coursePayload is FormData, send as FormData (for file uploads)
    // If it's a regular object, send as JSON with nested structure
    const headers = {};
    if (coursePayload instanceof FormData) {
      // FormData - let browser set Content-Type with boundary
      // Still try to add auth if available (backend might still require it)
      const token = localStorage.getItem("authToken");
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    } else {
      // JSON payload - match API specification
      headers["Content-Type"] = "application/json";
      // Try auth if available
      const token = localStorage.getItem("authToken");
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    logger.debug("Sending request to:", `${API_BASE_URL}/courses`);
    logger.debug(
      "Payload type:",
      coursePayload instanceof FormData ? "FormData" : "JSON",
    );
    if (!(coursePayload instanceof FormData)) {
      logger.debug(
        "Payload structure:",
        JSON.stringify(coursePayload, null, 2),
      );
    }

    const response = await axios.post(
      `${API_BASE_URL}/courses`,
      coursePayload,
      {
        headers,
      },
    );

    // Handle API response structure: { success, message, data }
    const responseData = response.data;
    logger.debug("Course created successfully:", responseData);

    // Check if response indicates success
    if (responseData.success === false) {
      const error = new Error(responseData.message || "Course creation failed");
      error.response = response;
      throw error;
    }

    // Return the full response structure (includes success, message, data)
    return responseData;
  } catch (err) {
    logger.error("createCourse error:", err);
    logger.error("Error response:", err.response);
    logger.error("Error response data:", err.response?.data);
    logger.error("Error response status:", err.response?.status);

    // Extract error message from API response if available
    if (err.response?.data) {
      const apiError = err.response.data;
      if (apiError.message) {
        err.message = apiError.message;
      } else if (apiError.error) {
        err.message = apiError.error;
      }
    }
    throw err;
  }
};

// Create module
export const createModule = async (moduleData) => {
  await ensureSuperAdmin("create modules");
  try {
    const token = localStorage.getItem("authToken");
    const response = await axios.post(`${API_BASE_URL}/modules`, moduleData, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (err) {
    logger.error("createModule error:", err.response?.data || err.message);
    throw err;
  }
};

// Create topic
export const createTopic = async (topicData) => {
  await ensureSuperAdmin("create topics");
  try {
    const token = localStorage.getItem("authToken");
    const response = await axios.post(`${API_BASE_URL}/topics`, topicData, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (err) {
    logger.error("createTopic error:", err.response?.data || err.message);
    throw err;
  }
};
// =============== FEEDBACKS API ===================

export const getFeedbacks = () =>
  axios.get(`${API_BASE_URL}/feedbacks`, {
    headers: getAuthHeaders(),
  });

const extractFeedbackDetailPayload = (response) =>
  response?.data?.data || response?.data || {};

const hasFeedbackThreadArray = (payload) =>
  ["responses", "conversations", "feedback_responses", "response"].some(
    (key) => Array.isArray(payload?.[key]),
  );

export const getFeedbackById = async (id) => {
  let primaryResponse;

  try {
    primaryResponse = await axios.get(`${API_BASE_URL}/feedbacks/${id}`, {
      headers: getAuthHeaders(),
    });
  } catch (primaryErr) {
    return axios.get(`${API_BASE_URL}/feedback/${id}`, {
      headers: getAuthHeaders(),
    });
  }

  const primaryPayload = extractFeedbackDetailPayload(primaryResponse);
  if (hasFeedbackThreadArray(primaryPayload)) {
    return primaryResponse;
  }

  try {
    return await axios.get(`${API_BASE_URL}/feedback/${id}`, {
      headers: getAuthHeaders(),
    });
  } catch (secondaryErr) {
    return primaryResponse;
  }
};

export const updateFeedback = (id, status) =>
  axios.patch(
    `${API_BASE_URL}/feedbacks/${id}`,
    { status },
    { headers: getAuthHeaders() },
  );
export const sendResponse = async (feedbackId, message) => {
  await ensureSuperAdmin("respond to feedbacks");
  return axios.post(
    `${API_BASE_URL}/responses`,
    { feedback_id: feedbackId, body: message },
    { headers: getAuthHeaders() },
  );
};
// =============== FORUMS API ===================

// api.js

export const addForum = async (payload) => {
  try {
    const res = await api.post(
      `/forums`,
      {
        course_id: payload.course_id,
        admin_id: payload.admin_id,
        title: payload.title,
        body: payload.body, // instead of description
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${payload.token}`,
        },
      },
    );
    return res.data;
  } catch (err) {
    logger.error("Error adding forum:", err);
    throw err;
  }
};

// ✅ Get Forums (with optional course_id filter)
export const getForums = async (courseId) => {
  try {
    const res = await api.get(`/forums`);
    return Array.isArray(res.data.data) ? res.data.data : [];
  } catch (err) {
    logger.error("Error fetching forums:", err);
    return [];
  }
};

// ✅ Get Forum Details
export const getForumDetails = async (id) => {
  try {
    const res = await api.get(`/forums/${id}`);
    return res.data.data;
  } catch (err) {
    logger.error("Error fetching forum details:", err);
    return null;
  }
};

// ✅ Add Discussion
export const addDiscussion = async (forumId, payload) => {
  try {
    const res = await api.post(
      `/discussions`,
      { forum_id: forumId, ...payload },
      { headers: { "Content-Type": "application/json", ...getAuthHeaders() } },
    );
    return res.data;
  } catch (err) {
    logger.error("Error adding discussion:", err.response?.data || err);
    throw err;
  }
};

// ✅ Get Discussions by Forum
export const getDiscussions = async (forumId) => {
  try {
    const res = await api.get(`/discussions`, {
      params: { forum_id: forumId },
    });
    return Array.isArray(res.data.data) ? res.data.data : [];
  } catch (err) {
    logger.error("Error fetching discussions:", err);
    return [];
  }
};

export const deleteForum = async (forumId) => {
  await ensureSuperAdmin("delete forum");
  return axios.delete(`${API_BASE_URL}/forums/${forumId}`, {
    headers: getAuthHeaders(),
  });
};
export const deleteDiscussion = async (discussionId) => {
  await ensureSuperAdmin("delete discussion");
  return axios.delete(`${API_BASE_URL}/discussions/${discussionId}`, {
    headers: getAuthHeaders(),
  });
};

// ---------------- UPDATE STAFF PASSWORD ----------------
export const updateStaffPassword = async (staffId, newPassword) => {
  await ensureSuperAdmin("update password");
  try {
    const response = await api.post(
      `/update/password/staff`,
      {
        staff_id: staffId,
        password: newPassword,
      },
      { headers: getAuthHeaders() },
    );
    return response.data;
  } catch (err) {
    logger.error("Failed to update staff password:", err.response?.data || err);
    throw err.response?.data || { message: "Password update failed" };
  }
};

// ---------------- CHANGE PASSWORD (CURRENT USER) ----------------
export const changePassword = async (newPassword) => {
  try {
    const user = getCurrentUser();
    // Determine user type - if it's an admin (super_admin or admin), use 'admin', otherwise 'staff'
    const userType =
      user?.role === "super_admin" ||
      user?.role === "admin" ||
      user?.is_super_admin === 1
        ? "admin"
        : "staff";
    const response = await api.post(
      `/update/password/${userType}`,
      {
        password: newPassword,
        password_confirmation: newPassword,
      },
      { headers: getAuthHeaders() },
    );
    return response.data;
  } catch (err) {
    logger.error("Failed to change password:", err.response?.data || err);
    // Handle Laravel validation errors
    if (err.response?.data?.errors) {
      const errors = err.response.data.errors;
      const firstError = Object.values(errors)[0];
      const errorMessage = Array.isArray(firstError)
        ? firstError[0]
        : firstError;
      throw { error: errorMessage, errors: errors };
    }
    throw err.response?.data || { message: "Password change failed" };
  }
};
// Get Test/Quiz by ID
export const getTestById = async (id) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/tests/${id}`, {
      headers: getAuthHeaders(),
    });

    const responseData = response.data;
    logger.debug("Test fetched successfully:", responseData);

    if (responseData.success === false) {
      const error = new Error(responseData.message || "Failed to fetch test");
      error.response = response;
      throw error;
    }

    return responseData.data || responseData;
  } catch (err) {
    logger.error("Failed to fetch test:", err.response?.data || err);
    if (err.response?.data) {
      const apiError = err.response.data;
      if (apiError.message) {
        err.message = apiError.message;
      }
    }
    throw err;
  }
};

export const updateTestStatus = async (id, data) => {
  const res = await api.put(`/tests/${id}`, data, {
    headers: { "Content-Type": "application/json" },
  });
  return res.data.data;
};

export const getCourseFullDetails = async (courseId) => {
  try {
    // Fetch course details - backend returns course with modules and category
    const courseResponse = await api.get(`/courses/${courseId}`);
    const courseData = courseResponse.data.data || courseResponse.data;
    // The backend returns course with modules already included via ->with(['modules', 'category'])
    let modules = [];
    if (courseData.modules && Array.isArray(courseData.modules)) {
      modules = courseData.modules;
    } else {
      // Fallback: try to fetch modules separately if not included
      try {
        const modulesResponse = await getModules(courseId);
        modules = normalizeModulesData(modulesResponse);
      } catch (err) {
        logger.warn("Could not fetch modules separately:", err);
        modules = [];
      }
    }

    return {
      course: courseData,
      modules: modules,
      modules_data: modules,
    };
  } catch (err) {
    logger.error("Error fetching course full details:", err);
    throw err;
  }
};

// Helper function to normalize modules data
const normalizeModulesData = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.data)) return raw.data;
  if (Array.isArray(raw?.modules)) return raw.modules;
  if (Array.isArray(raw?.data?.modules)) return raw.data.modules;
  return [];
};
// ---------------- FETCH ROLES ----------------
export const getRoles = async () => {
  try {
    // Try common endpoints for roles
    let response;
    try {
      response = await api.get(`/roles`, {
        headers: getAuthHeaders(),
      });
    } catch (err) {
      // Try alternative endpoint
      try {
        response = await api.get(`/admin/roles`, {
          headers: getAuthHeaders(),
        });
      } catch (err2) {
        // Fallback: return default roles if endpoint doesn't exist
        logger.warn("Roles endpoint not found, using default roles");
        return [
          { id: "admin", name: "Admin", value: "Admin" },
          { id: "super_admin", name: "Super Admin", value: "Super Admin" },
        ];
      }
    }

    // Handle different response structures
    const rolesData = response.data?.data || response.data || [];

    // Normalize roles to have consistent structure
    if (Array.isArray(rolesData) && rolesData.length > 0) {
      return rolesData.map((role) => ({
        id: role.id || role.value || role.name?.toLowerCase().replace(" ", "_"),
        name: role.name || role.label || role.value,
        value: role.value || role.name || role.label,
        description: role.description || "",
        permissions: Array.isArray(role.permissions) ? role.permissions : [],
      }));
    }
    // Fallback to default roles
    return [
      { id: "admin", name: "Admin", value: "Admin" },
      { id: "super_admin", name: "Super Admin", value: "Super Admin" },
    ];
  } catch (err) {
    logger.error("Failed to fetch roles:", err.response?.data || err);
    // Return default roles on error
    return [
      { id: "admin", name: "Admin", value: "Admin" },
      { id: "super_admin", name: "Super Admin", value: "Super Admin" },
    ];
  }
};

// ---------------- ROLE & PERMISSION MANAGEMENT (super admin only) ----------------
export const getAllPermissions = async () => {
  try {
    const response = await api.get("/permissions", {
      headers: getAuthHeaders(),
    });
    const data = response.data?.data || response.data || [];
    return Array.isArray(data) ? data : [];
  } catch (err) {
    logger.error("Failed to fetch permissions:", err.response?.data || err);
    throw err.response?.data || { message: "Failed to fetch permissions" };
  }
};

export const createRole = async ({ name, description, permissionIds }) => {
  try {
    const response = await api.post(
      "/roles",
      { name, description, permissions: permissionIds || [] },
      { headers: getAuthHeaders() },
    );
    return response.data?.data || response.data;
  } catch (err) {
    logger.error("Failed to create role:", err.response?.data || err);
    throw err.response?.data || { message: "Failed to create role" };
  }
};

export const updateRole = async (id, { name, description, permissionIds }) => {
  try {
    const response = await api.put(
      `/roles/${id}`,
      { name, description, permissions: permissionIds || [] },
      { headers: getAuthHeaders() },
    );
    return response.data?.data || response.data;
  } catch (err) {
    logger.error("Failed to update role:", err.response?.data || err);
    throw err.response?.data || { message: "Failed to update role" };
  }
};

export const deleteRole = async (id) => {
  try {
    const response = await api.delete(`/roles/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (err) {
    logger.error("Failed to delete role:", err.response?.data || err);
    throw err.response?.data || { message: "Failed to delete role" };
  }
};

// ---------------- CREATE ADMIN ----------------
export async function createAdmin(data) {
  await ensureSuperAdmin("create admin"); // Only super admins can create admins

  // Determine role_id from role value
  // If role is a number (ID), use it directly; otherwise try to match by name
  let roleId = null;
  if (typeof data.role === "number" || !isNaN(parseInt(data.role))) {
    roleId = parseInt(data.role);
  } else {
    // Try to find role by name/value
    const roleLower = (data.role || "").toLowerCase();
    if (roleLower.includes("super") || roleLower === "super_admin") {
      roleId = 1; // Assuming 1 is super admin role_id
    } else {
      roleId = 2; // Assuming 2 is admin role_id
    }
  }

  const payload = {
    firstname: data.firstName?.trim() || "",
    lastname: data.lastName?.trim() || "",
    othernames: data.middleName?.trim() || null,
    email: data.email?.trim() || "",
    role_id: roleId,
    is_super_admin:
      data.role &&
      (data.role.toString().toLowerCase().includes("super") ||
        data.role.toString().toLowerCase() === "super_admin")
        ? 1
        : 0,
  };

  const extractErrorMessage = (errorBody) => {
    if (!errorBody) return null;
    if (typeof errorBody === "string") return errorBody;
    if (errorBody.message) return errorBody.message;
    if (errorBody.error) {
      if (typeof errorBody.error === "string") return errorBody.error;
      if (typeof errorBody.error === "object") {
        const firstKey = Object.keys(errorBody.error)[0];
        const value = errorBody.error[firstKey];
        if (Array.isArray(value)) return value[0];
        if (typeof value === "string") return value;
      }
    }
    if (errorBody.errors) {
      const firstKey = Object.keys(errorBody.errors)[0];
      const value = errorBody.errors[firstKey];
      if (Array.isArray(value)) return value[0];
      if (typeof value === "string") return value;
    }
    return null;
  };

  try {
    const res = await api.post(`/auth/register/admin`, payload, {
      headers: getAuthHeaders(),
    });
    return res.data;
  } catch (err) {
    const message =
      extractErrorMessage(err.response?.data) ||
      err.response?.data?.error ||
      err.message ||
      "Failed to create admin";
    logger.error("❌ Error creating admin:", err.response?.data || err.message);
    throw new Error(message);
  }
}
export const getAdmins = async (filters = {}) => {
  try {
    const params = {};
    if (filters.from) params.from = filters.from;
    if (filters.to) params.to = filters.to;
    if (filters.search) params.search = filters.search;

    const response = await api.get(`/get/admins`, {
      headers: getAuthHeaders(),
      params,
    });
    return response.data?.data || response.data || [];
  } catch (err) {
    logger.error("Failed to fetch admins:", err.response?.data || err);
    throw err.response?.data || { message: "Failed to fetch admins" };
  }
};

// Paginated version of getAdmins
export const getAdminsPaginated = async (page = 1, limit = 25, search = "") => {
  try {
    const params = new URLSearchParams({ page, per_page: limit });
    if (search) params.append("search", search);

    const response = await api.get(`/get/admins?${params.toString()}`, {
      headers: getAuthHeaders(),
    });
    const data = response.data;

    // Handle both paginated and non-paginated responses
    if (data.current_page !== undefined) {
      // Paginated response
      return {
        data: data.data || [],
        meta: {
          page: data.current_page || page,
          limit: data.per_page || limit,
          total: data.total || 0,
          totalPages: data.last_page || 1,
        },
      };
    } else {
      // Non-paginated response - do client-side pagination
      const allData = data.data || data || [];
      const filtered = search
        ? allData.filter((item) => {
            const name =
              `${item.firstname || ""} ${item.lastname || ""}`.toLowerCase();
            const email = (item.email || "").toLowerCase();
            return (
              name.includes(search.toLowerCase()) ||
              email.includes(search.toLowerCase())
            );
          })
        : allData;

      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;

      return {
        data: filtered.slice(startIndex, endIndex),
        meta: {
          page,
          limit,
          total: filtered.length,
          totalPages: Math.ceil(filtered.length / limit) || 1,
        },
      };
    }
  } catch (err) {
    logger.error("Failed to fetch admins:", err.response?.data || err);
    throw err.response?.data || { message: "Failed to fetch admins" };
  }
};

export const getAdminById = async (id) => {
  try {
    const response = await api.get(`/admins/${id}`, {
      headers: getAuthHeaders(),
    });
    const admin = response.data?.data || response.data;

    if (!admin) {
      throw new Error("Admin not found");
    }

    return {
      ...admin,
      email: admin.email,
      phone_number: admin.phone_number || "N/A",
      role: admin.role || "admin",
    };
  } catch (err) {
    logger.error("getAdminById error:", err.response?.data || err.message);
    throw err;
  }
};
export async function updateAdmin(id, data) {
  await ensureSuperAdmin("Update Admins");

  const payload = {
    firstname: data.firstname,
    lastname: data.lastname,
    email: data.email,
    phone_number: data.phone_number || null,
  };
  if (data.password) {
    payload.password = data.password;
  }
  if (data.job_title_id !== undefined) {
    payload.job_title_id = data.job_title_id || null;
  }

  const res = await api.put(`/admins/${id}`, payload, {
    headers: getAuthHeaders(),
  });
  return res.data;
}

/**
 * Sync, attach, or detach roles on an admin.
 * @param {number|string} adminId
 * @param {number[]} roleIds
 * @param {'sync'|'attach'|'detach'} action - defaults to 'sync' (replaces the full role set)
 */
export async function syncAdminRoles(adminId, roleIds, action = "sync") {
  await ensureSuperAdmin("Manage Admin Roles");

  const res = await api.post(
    `/admins/${adminId}/roles`,
    { role_ids: roleIds, action },
    { headers: getAuthHeaders() },
  );
  return res.data;
}

// Helper function to decode JWT token (for debugging)
const decodeJWT = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join(""),
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

// ---------------- SUSPEND/ACTIVATE ADMIN ----------------
export async function suspendAdmin(id, suspend = true) {
  await ensureSuperAdmin("Suspend/Activate Admins");
  // Debug: Check both localStorage and JWT token
  const currentUser = getCurrentUser();
  const token = localStorage.getItem("authToken");
  const tokenPayload = token ? decodeJWT(token) : null;

  logger.debug("=== DEBUG: Admin Suspend/Activate ===");
  logger.debug("LocalStorage user:", currentUser);
  logger.debug("LocalStorage role:", currentUser?.role);
  logger.debug("LocalStorage is_super_admin:", currentUser?.is_super_admin);
  logger.debug("JWT Token payload:", tokenPayload);
  logger.debug("JWT role:", tokenPayload?.role);
  logger.debug("JWT is_super_admin:", tokenPayload?.is_super_admin);
  logger.debug("=====================================");

  // If JWT doesn't have role but localStorage does, warn user
  if (
    currentUser &&
    (currentUser.role === "super_admin" || currentUser.is_super_admin === 1)
  ) {
    if (
      !tokenPayload ||
      (tokenPayload.role !== "super_admin" && tokenPayload.is_super_admin !== 1)
    ) {
      logger.warn(
        "⚠️ WARNING: localStorage says you are a super admin, but JWT token does not. You may need to log out and log back in to refresh your token.",
      );
    }
  }

  try {
    // First, try to get the admin to get all current fields
    let adminData = null;
    try {
      adminData = await getAdminById(id);
    } catch (err) {
      logger.warn(
        "Could not fetch admin data, proceeding with status update only:",
        err,
      );
    }

    // Try different possible endpoints and payload formats
    let response;
    let lastError;

    // Try 1: Using PUT with is_active (most common and standard approach)
    try {
      response = await api.put(
        `/admins/${id}`,
        {
          is_active: suspend ? 0 : 1,
        },
        {
          headers: getAuthHeaders(),
        },
      );
      logger.debug(
        `Successfully ${suspend ? "suspended" : "activated"} admin ${id} via PUT /admins/${id}`,
      );
      return response.data;
    } catch (err) {
      lastError = err;
      // Only log if it's not a 404 (which means endpoint doesn't exist)
      if (err.response?.status !== 404) {
        logger.warn(
          "PUT with is_active failed:",
          err.response?.status,
          err.response?.data?.message || err.message,
        );
      }
    }

    // Try 2: Using updateAdmin pattern with all fields + status
    if (adminData) {
      try {
        const payload = {
          firstname: adminData.firstname || adminData.first_name,
          lastname: adminData.lastname || adminData.last_name,
          email: adminData.email,
          phone_number: adminData.phone_number || adminData.phone || null,
          job_title_id: adminData.job_title_id || null,
          role: adminData.role || "admin",
          is_active: suspend ? 0 : 1,
        };
        response = await api.put(`/admins/${id}`, payload, {
          headers: getAuthHeaders(),
        });
        logger.debug(
          `Successfully ${suspend ? "suspended" : "activated"} admin ${id} via PUT with full payload`,
        );
        return response.data;
      } catch (err) {
        lastError = err;
        if (err.response?.status !== 404) {
          logger.warn(
            "Failed to update with full payload:",
            err.response?.status,
            err.response?.data?.message || err.message,
          );
        }
      }
    }

    // Try 3: Using PATCH with is_active (some APIs prefer PATCH for partial updates)
    try {
      response = await api.patch(
        `/admins/${id}`,
        {
          is_active: suspend ? 0 : 1,
        },
        {
          headers: getAuthHeaders(),
        },
      );
      logger.debug(
        `Successfully ${suspend ? "suspended" : "activated"} admin ${id} via PATCH /admins/${id}`,
      );
      return response.data;
    } catch (err) {
      lastError = err;
      if (err.response?.status !== 404) {
        logger.warn(
          "PATCH with is_active failed:",
          err.response?.status,
          err.response?.data?.message || err.message,
        );
      }
    }

    // Try 4: Using a dedicated suspend endpoint (POST) - if backend implemented this
    try {
      const endpoint = suspend
        ? `/admins/${id}/suspend`
        : `/admins/${id}/activate`;
      response = await api.post(
        endpoint,
        {},
        {
          headers: getAuthHeaders(),
        },
      );
      logger.debug(
        `Successfully ${suspend ? "suspended" : "activated"} admin ${id} via POST ${endpoint}`,
      );
      return response.data;
    } catch (err) {
      lastError = err;
      if (err.response?.status !== 404) {
        logger.warn(
          "POST suspend endpoint failed:",
          err.response?.status,
          err.response?.data?.message || err.message,
        );
      }
    }

    // Try 5: Using is_suspended field
    try {
      response = await api.put(
        `/admins/${id}`,
        {
          is_suspended: suspend ? 1 : 0,
        },
        {
          headers: getAuthHeaders(),
        },
      );
      logger.debug(
        `Successfully ${suspend ? "suspended" : "activated"} admin ${id} via PUT with is_suspended`,
      );
      return response.data;
    } catch (err) {
      lastError = err;
      if (err.response?.status !== 404) {
        logger.warn(
          "PUT with is_suspended failed:",
          err.response?.status,
          err.response?.data?.message || err.message,
        );
      }
    }

    // Try 6: Using status field
    try {
      response = await api.put(
        `/admins/${id}`,
        {
          status: suspend ? "suspended" : "active",
        },
        {
          headers: getAuthHeaders(),
        },
      );
      logger.debug(
        `Successfully ${suspend ? "suspended" : "activated"} admin ${id} via PUT with status`,
      );
      return response.data;
    } catch (err) {
      lastError = err;
      if (err.response?.status !== 404) {
        logger.warn(
          "PUT with status failed:",
          err.response?.status,
          err.response?.data?.message || err.message,
        );
      }
    }

    // All attempts failed
    logger.error(
      "All suspend endpoint attempts failed. Last error:",
      lastError?.response?.status,
      lastError?.response?.data || lastError?.message,
    );
    const errorDetails = lastError?.response?.data
      ? JSON.stringify(lastError.response.data)
      : lastError?.message || "Unknown error";
    throw new Error(
      `Failed to suspend/activate admin. Backend returned ${lastError?.response?.status || "unknown status"}. Details: ${errorDetails}`,
    );
  } catch (err) {
    logger.error(
      "Failed to suspend/activate admin:",
      err.response?.data || err,
    );
    const errorMessage =
      err.response?.data?.message ||
      err.response?.data?.error ||
      err.message ||
      "Failed to suspend/activate admin";
    throw new Error(errorMessage);
  }
}
export async function deleteAdmin(id) {
  await ensureSuperAdmin("Delete Admins");

  try {
    // Try DELETE first (standard REST approach)
    try {
      const res = await api.delete(`/admins/${id}`, {
        headers: getAuthHeaders(),
      });
      return res.data;
    } catch (deleteErr) {
      // If DELETE not supported (405), try soft delete via PUT
      if (deleteErr.response?.status === 405) {
        logger.debug("DELETE not supported, trying soft delete via PUT");

        // Soft delete by setting is_active to 0
        const res = await api.put(
          `/admins/${id}`,
          {
            is_active: 0,
            is_deleted: 1,
          },
          {
            headers: getAuthHeaders(),
          },
        );
        return res.data;
      }
      throw deleteErr;
    }
  } catch (err) {
    logger.error("Failed to delete admin:", err.response?.data || err);
    const errorMessage =
      err.response?.data?.message ||
      err.response?.data?.error ||
      err.message ||
      "Failed to delete admin. Please try again.";
    throw new Error(errorMessage);
  }
}

// ---------------- SUSPEND/ACTIVATE LEARNER (HEALTH WORKER) ----------------
export async function suspendLearner(id, suspend = true) {
  await ensureSuperAdmin("Suspend/Activate Learners");

  try {
    // Try different possible endpoints and payload formats
    let response;

    // Try 1: Using is_active field (false = suspended)
    try {
      response = await api.put(
        `/learners/${id}`,
        {
          is_active: suspend ? 0 : 1,
        },
        {
          headers: getAuthHeaders(),
        },
      );
    } catch (err) {
      // Try 2: Using is_suspended field
      try {
        response = await api.put(
          `/learners/${id}`,
          {
            is_suspended: suspend ? 1 : 0,
          },
          {
            headers: getAuthHeaders(),
          },
        );
      } catch (err2) {
        // Try 3: Using status field
        try {
          response = await api.put(
            `/learners/${id}`,
            {
              status: suspend ? "suspended" : "active",
            },
            {
              headers: getAuthHeaders(),
            },
          );
        } catch (err3) {
          // Try 4: Using a dedicated suspend endpoint
          try {
            const endpoint = suspend
              ? `/learners/${id}/suspend`
              : `/learners/${id}/activate`;
            response = await api.post(
              endpoint,
              {},
              {
                headers: getAuthHeaders(),
              },
            );
          } catch (err4) {
            logger.error("All suspend endpoint attempts failed:", {
              err,
              err2,
              err3,
              err4,
            });
            throw new Error(
              "Failed to suspend/activate learner. Please check backend API.",
            );
          }
        }
      }
    }

    return response.data;
  } catch (err) {
    logger.error(
      "Failed to suspend/activate learner:",
      err.response?.data || err,
    );
    const errorMessage =
      err.response?.data?.message ||
      err.response?.data?.error ||
      err.message ||
      "Failed to suspend/activate learner";
    throw new Error(errorMessage);
  }
}

// ---------------- DELETE LEARNER (HEALTH WORKER) ----------------
export async function deleteLearner(id) {
  await ensureSuperAdmin("Delete Learners");

  try {
    const res = await api.delete(`/learners/${id}`, {
      headers: getAuthHeaders(),
    });
    return res.data;
  } catch (err) {
    logger.error("Failed to delete learner:", err.response?.data || err);
    const errorMessage =
      err.response?.data?.message ||
      err.response?.data?.error ||
      err.message ||
      "Failed to delete learner";
    throw new Error(errorMessage);
  }
}

// ---------------- ACTIVITY LOGS ----------------
export const getActivityLogs = async (
  page = 1,
  perPage = 15,
  causerId = null,
) => {
  try {
    const params = { page, per_page: perPage };
    if (causerId) {
      params.causer_id = causerId;
    }
    const response = await api.get(`/activity/logs`, {
      params,
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (err) {
    logger.error("Failed to fetch activity logs:", err.response?.data || err);
    throw err.response?.data || { message: "Failed to fetch activity logs" };
  }
};

// ---------------- ANALYTICS/DASHBOARD API ----------------
export const getAdminDashboard = async () => {
  try {
    const response = await api.get(`/admin/dashboard`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (err) {
    logger.error("Failed to fetch admin dashboard:", err.response?.data || err);
    throw err.response?.data || { message: "Failed to fetch dashboard data" };
  }
};

// ---------------- COURSE ENROLLMENTS ----------------
export const getCourseEnrollments = async (courseId) => {
  try {
    const response = await api.get(`/enrolments`, {
      params: { course_id: courseId },
      headers: getAuthHeaders(),
    });
    return response.data.data || [];
  } catch (err) {
    logger.error(
      "Failed to fetch course enrollments:",
      err.response?.data || err,
    );
    throw (
      err.response?.data || { message: "Failed to fetch course enrollments" }
    );
  }
};

export const getEnrolmentByCourseAndStaff = async (courseId, staffId) => {
  try {
    const response = await api.get(`/enrolments`, {
      params: { course_id: courseId, staff_id: staffId },
      headers: getAuthHeaders(),
    });

    const payload = response.data?.data ?? response.data;
    if (Array.isArray(payload)) {
      return payload[0] || null;
    }

    if (Array.isArray(payload?.data)) {
      return payload.data[0] || null;
    }

    return payload || null;
  } catch (err) {
    logger.error(
      "Failed to fetch enrolment by course and staff:",
      err.response?.data || err,
    );
    throw (
      err.response?.data || { message: "Failed to fetch enrolment details" }
    );
  }
};

// ---------------- ACTIVE/INACTIVE USERS METRIC ----------------
/**
 * Get active/inactive users metric based on last 30 days activity
 * Active = users who have logged in or performed activity within last 30 days
 * Inactive = users with no activity within last 30 days
 */
export const getActiveUsersMetric = async () => {
  try {
    // First try dedicated endpoint if backend supports it
    try {
      const response = await api.get("/admin/metrics/active-users", {
        headers: getAuthHeaders(),
      });
      if (response.data) {
        return response.data.data || response.data;
      }
    } catch (endpointErr) {
      logger.debug(
        "Active users endpoint not available, computing from activity logs",
      );
    }

    // Fallback: Compute from activity logs and learners data
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const thirtyDaysAgoStr = thirtyDaysAgo.toISOString().split("T")[0];

    // Get all learners
    let learners = [];
    try {
      const learnersData = await getLearners();
      learners = Array.isArray(learnersData) ? learnersData : [];
    } catch (err) {
      logger.warn("Could not fetch learners for active metric:", err);
    }

    // Get recent activity logs (last 30 days)
    let recentActivityUserIds = new Set();
    try {
      // Fetch activity logs pages to find active users
      const logsResponse = await getActivityLogs(1, 100);
      const logs = logsResponse.data || logsResponse || [];

      logs.forEach((log) => {
        const logDate = new Date(log.created_at || log.timestamp);
        if (logDate >= thirtyDaysAgo && log.causer_id) {
          recentActivityUserIds.add(log.causer_id);
        }
      });
    } catch (err) {
      logger.warn("Could not fetch activity logs:", err);
    }

    // Compute metrics
    let activeCount = 0;
    let inactiveCount = 0;

    learners.forEach((learner) => {
      const lastLoginAt =
        learner.last_login_at || learner.last_activity_at || learner.updated_at;
      const lastLoginDate = lastLoginAt ? new Date(lastLoginAt) : null;

      const hasRecentLogin = lastLoginDate && lastLoginDate >= thirtyDaysAgo;
      const hasRecentActivity = recentActivityUserIds.has(learner.id);

      if (hasRecentLogin || hasRecentActivity) {
        activeCount++;
      } else {
        inactiveCount++;
      }
    });

    return {
      active: activeCount,
      inactive: inactiveCount,
      total: learners.length,
      percentActive:
        learners.length > 0
          ? Math.round((activeCount / learners.length) * 100)
          : 0,
      computedAt: new Date().toISOString(),
      periodDays: 30,
    };
  } catch (err) {
    logger.error("Failed to compute active users metric:", err);
    throw (
      err.response?.data || { message: "Failed to fetch active users metric" }
    );
  }
};

// ---------------- BLOG/CONTENT POSTS ----------------
const normalizeBlogPublishedFilter = (status = "all") => {
  if (status === "published") return 1;
  if (status === "draft") return 0;
  return null;
};

const buildBlogPayload = (data = {}) => {
  const hasBinaryFile =
    typeof File !== "undefined" && data.file instanceof File;

  if (!hasBinaryFile) {
    return {
      payload: {
        ...data,
        is_published: data.is_published ? 1 : 0,
      },
      headers: getAuthHeaders(),
    };
  }

  const formData = new FormData();
  Object.entries(data).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") return;
    if (key === "is_published") {
      formData.append(key, value ? "1" : "0");
      return;
    }
    formData.append(key, value);
  });

  return {
    payload: formData,
    headers: {
      ...getAuthHeaders(),
      "Content-Type": "multipart/form-data",
    },
  };
};

export const getBlogPosts = async (page = 1, limit = 10, status = "all") => {
  try {
    const params = new URLSearchParams({ page, per_page: limit });
    const isPublished = normalizeBlogPublishedFilter(status);
    if (isPublished !== null) {
      params.append("is_published", String(isPublished));
    }

    const response = await api.get(`/blogs?${params.toString()}`, {
      headers: getAuthHeaders(),
    });
    const data = response.data;
    const responseList = data.data || [];
    const paginationMeta = data.meta || data;

    return {
      data: responseList,
      meta: {
        page: paginationMeta.current_page || page,
        limit: paginationMeta.per_page || limit,
        total: paginationMeta.total || 0,
        totalPages: paginationMeta.last_page || 1,
      },
    };
  } catch (err) {
    logger.error("Failed to fetch blog posts:", err.response?.data || err);
    return { data: [], meta: { page: 1, limit: 10, total: 0, totalPages: 1 } };
  }
};

export const getBlogPostBySlug = async (slug) => {
  try {
    const response = await api.get(`/blogs/${slug}`, {
      headers: getAuthHeaders(),
    });
    return response.data.data || response.data;
  } catch (err) {
    logger.error("Failed to fetch blog post:", err.response?.data || err);
    throw err.response?.data || { message: "Failed to fetch blog post" };
  }
};

export const createBlogPost = async (data) => {
  try {
    const { payload, headers } = buildBlogPayload(data);
    logger.info("API create blog request", {
      endpoint: "/blogs",
      method: "POST",
      isMultipart: payload instanceof FormData,
    });
    const response = await api.post("/blogs", payload, {
      headers,
    });
    return response.data.data || response.data;
  } catch (err) {
    logger.error("Failed to create blog post:", err.response?.data || err);
    throw err.response?.data || { message: "Failed to create blog post" };
  }
};

export const updateBlogPost = async (id, data) => {
  try {
    const { payload, headers } = buildBlogPayload(data);
    const response = await api.put(`/blogs/${id}`, payload, {
      headers,
    });
    return response.data.data || response.data;
  } catch (err) {
    logger.error("Failed to update blog post:", err.response?.data || err);
    throw err.response?.data || { message: "Failed to update blog post" };
  }
};

export const deleteBlogPost = async (id) => {
  try {
    const response = await api.delete(`/blogs/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (err) {
    logger.error("Failed to delete blog post:", err.response?.data || err);
    throw err.response?.data || { message: "Failed to delete blog post" };
  }
};

export const getBlogComments = async (blogId, page = 1, limit = 50) => {
  try {
    const params = new URLSearchParams({
      blog_id: String(blogId),
      page: String(page),
      per_page: String(limit),
    });
    const response = await api.get(`/blog_comments?${params.toString()}`, {
      headers: getAuthHeaders(),
    });

    const data = response.data;
    const responseList = data.data || [];
    const paginationMeta = data.meta || data;

    return {
      data: responseList,
      meta: {
        page: paginationMeta.current_page || page,
        limit: paginationMeta.per_page || limit,
        total: paginationMeta.total || responseList.length,
        totalPages: paginationMeta.last_page || 1,
      },
    };
  } catch (err) {
    logger.error("Failed to fetch blog comments:", err.response?.data || err);
    return { data: [], meta: { page: 1, limit: 50, total: 0, totalPages: 1 } };
  }
};

export const createBlogComment = async (data) => {
  try {
    const response = await api.post("/blog_comments", data, {
      headers: getAuthHeaders(),
    });
    return response.data.data || response.data;
  } catch (err) {
    logger.error("Failed to create blog comment:", err.response?.data || err);
    throw err.response?.data || { message: "Failed to create blog comment" };
  }
};

export const deleteBlogComment = async (id) => {
  try {
    const response = await api.delete(`/blog_comments/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (err) {
    logger.error("Failed to delete blog comment:", err.response?.data || err);
    throw err.response?.data || { message: "Failed to delete blog comment" };
  }
};

// ---------------- CERTIFICATE REVIEW ----------------
export const getCertificates = async (status = "all", page = 1, limit = 25) => {
  try {
    const params = new URLSearchParams({ page, per_page: limit });
    if (status && status !== "all") {
      params.append("status", status);
    }
    const response = await api.get(`/certificates?${params.toString()}`, {
      headers: getAuthHeaders(),
    });
    const data = response.data;
    return {
      data: data.data || [],
      meta: {
        page: data.current_page || page,
        limit: data.per_page || limit,
        total: data.total || 0,
        totalPages: data.last_page || 1,
      },
    };
  } catch (err) {
    logger.error("Failed to fetch certificates:", err.response?.data || err);
    return { data: [], meta: { page: 1, limit: 25, total: 0, totalPages: 1 } };
  }
};

export const getCertificateById = async (id) => {
  try {
    const response = await api.get(`/certificates/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data.data || response.data;
  } catch (err) {
    logger.error("Failed to fetch certificate:", err.response?.data || err);
    throw err.response?.data || { message: "Failed to fetch certificate" };
  }
};

// EnRiCH DLP Assistant - send a message to the AI assistant endpoint
export const sendAssistantMessage = async (message, conversationHistory = [], signal = null) => {
  try {
    const response = await api.post('/ai/care-assistant', {
      message,
      conversation_history: conversationHistory,
    }, {
      headers: getAuthHeaders(),
      signal,
    });
    return response.data.data || response.data;
  } catch (err) {
    if (err.name === 'CanceledError' || err.name === 'AbortError') throw err;
    logger.error('Assistant API error:', err.response?.data || err);
    throw err;
  }
};

export const approveCertificate = async (id, approved, reason = '') => {
  try {
    const action = approved ? "approve" : "reject";
    const response = await api.post(
      `/certificates/${id}/${action}`,
      {
        reason: reason,
      },
      {
        headers: getAuthHeaders(),
      },
    );
    return response.data.data || response.data;
  } catch (err) {
    logger.error(
      `Failed to ${approved ? "approve" : "reject"} certificate:`,
      err.response?.data || err,
    );
    throw (
      err.response?.data || {
        message: `Failed to ${approved ? "approve" : "reject"} certificate`,
      }
    );
  }
};
